import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Mail, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", subject: "", message: "" };

export const Support = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in every field so Bast can help.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! Bast will get back to you soon.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-black/10 bg-[#faf4e0]/60 px-5 py-3.5 text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/40";

  return (
    <section id="support" className="relative py-24 sm:py-32">
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-12 px-6 lg:grid-cols-2 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-heading text-sm font-bold uppercase tracking-widest text-emerald-600">
            Support
          </p>
          <h2 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
            Got a question? Bast is listening.
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Whether it's a bug, a billing question, or a feature you'd love to see — drop us a note
            and we'll get back to you. No bots, no hold music.
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-4 rounded-2xl border border-white/60 bg-white p-4 shadow-lg shadow-black/5">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                <Mail size={20} />
              </span>
              <div>
                <p className="font-heading font-bold text-gray-900">Email us</p>
                <p className="text-sm text-gray-600">support@purrse.app</p>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-2xl border border-white/60 bg-white p-4 shadow-lg shadow-black/5">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                <MessageCircle size={20} />
              </span>
              <div>
                <p className="font-heading font-bold text-gray-900">Typical reply time</p>
                <p className="text-sm text-gray-600">Within 1 business day</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="support-form"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-3xl border border-white/60 bg-white p-7 shadow-xl shadow-black/5 sm:p-9"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">Name</label>
              <input
                data-testid="support-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Jane Whiskers"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">Email</label>
              <input
                data-testid="support-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="jane@example.com"
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">Subject</label>
            <input
              data-testid="support-subject"
              value={form.subject}
              onChange={update("subject")}
              placeholder="How can we help?"
              className={inputCls}
            />
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">Message</label>
            <textarea
              data-testid="support-message"
              value={form.message}
              onChange={update("message")}
              rows={5}
              placeholder="Tell us what's on your mind…"
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="support-submit"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-8 py-4 font-heading text-base font-bold text-white shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5 hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send size={18} />
            {loading ? "Sending…" : "Send message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
